import { useEffect, useRef, useState } from 'react';
import { api } from './api';

export interface ActiveJob {
  id: string;
  filename: string;
  status: 'pending' | 'processing' | 'importing';
  pages_done: number;
  pages_total: number;
  statements_ready: number;
  reading_pages: number[];
  blocks_pending: number;
  started_at: string;
}

/**
 * Polls /file-storage/active-jobs while the tab is visible. Pauses when hidden
 * and refetches immediately on return. A failed poll keeps the last known list.
 */
export function useActiveOcrJobs(pollMs = 5000): { jobs: ActiveJob[]; loading: boolean } {
  const [jobs, setJobs] = useState<ActiveJob[]>([]);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await api('/file-storage/active-jobs') as { data: ActiveJob[] };
        if (!cancelled) setJobs(res?.data ?? []);
      } catch {
        /* transient — keep the last known list */
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    function start() {
      if (timerRef.current) return;
      void poll();
      timerRef.current = setInterval(poll, pollMs);
    }
    function stop() {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') start();
      else stop();
    }

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pollMs]);

  return { jobs, loading };
}
